import PatientWatchTM from "../components/common/PatientWatchTM";
import type { BreadcrumbItemT } from "../features/navigation/components/Breadcrumb";
import type { RouteT } from "../route-types";

export type NavT = {
    backRoute: BreadcrumbItemT;
    breadcrumbRoutes: BreadcrumbItemT[];
    setNav: (items: BreadcrumbItemT[]) => void;
    reset: () => void;
    /** Reset only if `key` still matches the last `setNav` call; lets an unmounting page clear its own crumbs without wiping a successor's. */
    resetIfCurrent: (key: string) => void;
};

export function getPenultimateEntry(arr: BreadcrumbItemT[]): BreadcrumbItemT {
    const homeCrumb = {
        label: "Home",
        route: { to: "/", params: {}, search: {} },
    } as BreadcrumbItemT;
    try {
        if (!arr.length) {
            return homeCrumb;
        }
        if (arr.length < 2) {
            return arr[0] as BreadcrumbItemT;
        }
        return arr[arr.length - 2] as BreadcrumbItemT;
    } catch (e) {
        console.error(e);
        return homeCrumb;
    }
}

export const homeRoute = {
    label: <PatientWatchTM />,
    route: { to: "/" as RouteT, params: {}, search: {} },
};

let navLastSetKey: string | null = null;

export const nav: NavT = {
    backRoute: homeRoute,
    breadcrumbRoutes: [homeRoute],
    setNav: (items: BreadcrumbItemT[]) => {
        nav.breadcrumbRoutes = items;
        nav.backRoute = getPenultimateEntry(items);
        navLastSetKey = serializeBreadcrumbPath(items);
    },
    reset: () => {
        nav.breadcrumbRoutes = [homeRoute];
        nav.backRoute = homeRoute;
        navLastSetKey = null;
    },
    resetIfCurrent: (key: string) => {
        if (navLastSetKey === key) nav.reset();
    },
};

import React from "react";

export const NavContext = React.createContext<NavT>({
    backRoute: homeRoute,
    breadcrumbRoutes: [homeRoute],
    setNav: (_items: BreadcrumbItemT[]) => {},
    reset: () => {},
    resetIfCurrent: (_key: string) => {},
});

export function NavProvider({ children }: { children: React.ReactNode }) {
    const [backRoute, setBackRoute] = React.useState<BreadcrumbItemT>(homeRoute);
    const [breadcrumbRoutes, setBreadcrumbRoutes] = React.useState<BreadcrumbItemT[]>([homeRoute]);
    const lastSetKeyRef = React.useRef<string | null>(null);

    const setNav = React.useCallback((items: BreadcrumbItemT[]) => {
        setBreadcrumbRoutes(items);
        setBackRoute(getPenultimateEntry(items));
        lastSetKeyRef.current = serializeBreadcrumbPath(items);
    }, []);

    const reset = React.useCallback(() => {
        setBreadcrumbRoutes([homeRoute]);
        setBackRoute(homeRoute);
        lastSetKeyRef.current = null;
    }, []);

    const resetIfCurrent = React.useCallback(
        (key: string) => {
            if (lastSetKeyRef.current === key) reset();
        },
        [reset]
    );

    const value = React.useMemo(
        () => ({ backRoute, breadcrumbRoutes, setNav, reset, resetIfCurrent }),
        [backRoute, breadcrumbRoutes, setNav, reset, resetIfCurrent]
    );

    return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
}

function serializeBreadcrumbPath(path: BreadcrumbItemT[]): string {
    return JSON.stringify(
        path.map((item) => ({
            label: item.label,
            route: item.route,
            status: item.status,
            options: item.options?.map((option) => ({ label: option.label, route: option.route })),
        }))
    );
}

export function useNav(path?: BreadcrumbItemT[]) {
    const context = React.useContext(NavContext);

    if (!context) {
        throw new Error("useNav must be used within a NavProvider");
    }

    const { setNav, resetIfCurrent } = context;
    const pathKey = path ? serializeBreadcrumbPath(path) : null;

    React.useEffect(() => {
        if (!path || !pathKey) return;
        setNav(path);
        // Clear our crumbs on unmount (unless a successor already set its own)
        // so pages without useNav never show a stale trail.
        return () => resetIfCurrent(pathKey);
    }, [pathKey, setNav, resetIfCurrent]);

    return context;
}
